/**
 * Resource Stats Hooks
 * React Query hooks for system-wide resource statistics
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { resourcesApi } from '@/lib/api/resources';

/**
 * Hook to fetch system-wide resource statistics
 */
export function useSystemResourceStats(
  includeTopUsers: boolean = true,
  topUsersLimit: number = 10
) {
  return useQuery({
    queryKey: ['resource-system-stats', includeTopUsers, topUsersLimit],
    queryFn: () => resourcesApi.getSystemStats(includeTopUsers, topUsersLimit),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch resource stats by type
 */
export function useResourceStatsByType() {
  return useQuery({
    queryKey: ['resource-stats-by-type'],
    queryFn: () => resourcesApi.getStatsByType(),
    staleTime: 30000,
  });
}

/**
 * Hook to cleanup temp files
 */
export function useCleanupTempFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: { days_old?: number; dry_run?: boolean }) =>
      resourcesApi.cleanupTempFiles(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-system-stats'] });
      queryClient.invalidateQueries({ queryKey: ['resource-stats-by-type'] });
    },
  });
}

/**
 * Hook to cleanup expired artifacts
 */
export function useCleanupExpiredArtifacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: {
      artifact_type?: string;
      user_id?: string;
      delete_files?: boolean;
    }) => resourcesApi.cleanupExpiredArtifacts(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-system-stats'] });
      queryClient.invalidateQueries({ queryKey: ['resource-stats-by-type'] });
    },
  });
}
